import { clearClientSession } from "./api";

const profitStreamHealth = {
  healthy: false,
  resubRecent: false,
  lastUpdate: 0,
};
const WS_VERBOSE = process.env.REACT_APP_WS_VERBOSE === "true";

function logWs(...args) {
  if (WS_VERBOSE) {
    console.log(...args);
  }
}

function debugWs(...args) {
  if (WS_VERBOSE) {
    console.debug(...args);
  }
}

function markProfitHealthy() {
  profitStreamHealth.healthy = true;
  profitStreamHealth.resubRecent = false;
  profitStreamHealth.lastUpdate = Date.now();
}

function markProfitStale(resub = false) {
  profitStreamHealth.healthy = false;
  profitStreamHealth.resubRecent = resub;
}

function readMsEnv(name, fallback) {
  const value = Number(process.env[name]);
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

function getStoredAuthToken() {
  try {
    return window.localStorage.getItem("token") || null;
  } catch (error) {
    console.warn("Failed to read auth token for WebSocket connection:", error);
    return null;
  }
}

function buildAuthenticatedWsUrl(wsUrl, token) {
  const url = new URL(wsUrl, window.location.href);
  url.searchParams.set("token", token);
  return url.toString();
}

class WebSocketManager {
  constructor() {
    this.socket = null;
    this.accountSetId = null;
    this.reconnectInterval = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 15;
    this.baseReconnectDelay = 1000;
    this.heartbeatInterval = null;
    this.lastPongTime = 0;
    this.apiErrorThrottle = new Map();
    this.balanceUpdateBuffer = new Map();
    this.quoteUpdateBuffer = {};
    this.FRESH_QUOTE_TOLERANCE = readMsEnv("REACT_APP_QUOTE_FRESH_MS", 7000);
    this.TRADE_QUOTE_TOLERANCE = readMsEnv(
      "REACT_APP_TRADE_QUOTE_FRESH_MS",
      Math.min(this.FRESH_QUOTE_TOLERANCE, 5000),
    );
    this.healthCheckInterval = null;

    this.setupPerformanceOptimizations();
    this.startHealthCheck();
  }

  setupPerformanceOptimizations() {
    setInterval(() => {
      if (this.balanceUpdateBuffer.size > 0) {
        const balanceUpdates = Object.fromEntries(this.balanceUpdateBuffer);
        this.balanceUpdateBuffer.clear();
        this.emitBalanceBatch(balanceUpdates);
      }
    }, 100);

    setInterval(() => {
      if (Object.keys(this.quoteUpdateBuffer).length > 0) {
        const quoteUpdates = { ...this.quoteUpdateBuffer };
        this.quoteUpdateBuffer = {};
        this.emitQuoteBatch(quoteUpdates);
      }
    }, 100);
  }

  emitBalanceBatch(balanceUpdates) {
    const handlers = WS_BUS.handlers.get("balance_batch");
    const list = handlers ? Array.from(handlers) : [];
    list.forEach((fn) => {
      try {
        fn(balanceUpdates);
      } catch (err) {
        console.error("❌ Balance batch handler error:", err);
      }
    });
  }

  emitQuoteBatch(quoteUpdates) {
    const handlers = WS_BUS.handlers.get("quote_batch");
    const list = handlers ? Array.from(handlers) : [];
    list.forEach((fn) => {
      try {
        fn(quoteUpdates);
      } catch (err) {
        console.error("❌ Quote batch handler error:", err);
      }
    });
  }

  createBrokerKey(broker) {
    if (!broker) return null;
    return `${broker.id || broker._id}|${broker.terminal}|pos${broker.position}`;
  }

  isQuoteFresh(quote, tolerance = null) {
    if (!quote || !quote.timestamp) return false;
    const age = Date.now() - new Date(quote.timestamp).getTime();
    const maxAge = tolerance || this.FRESH_QUOTE_TOLERANCE;
    return age >= 0 && age < maxAge;
  }

  validateTradeQuotes(futureQuote, spotQuote) {
    return (
      this.isQuoteFresh(futureQuote, this.TRADE_QUOTE_TOLERANCE) &&
      this.isQuoteFresh(spotQuote, this.TRADE_QUOTE_TOLERANCE)
    );
  }

  shouldLogApiError(error) {
    const key = `${error.apiName}_${error.endpoint}`;
    const lastError = this.apiErrorThrottle.get(key);
    const now = Date.now();

    if (!lastError || now - lastError > 30000) {
      this.apiErrorThrottle.set(key, now);
      return true;
    }
    return false;
  }

  checkWebSocketHealth() {
    const activeSocket = this.socket || socket;
    if (!activeSocket) return false;
    if (activeSocket.readyState !== WebSocket.OPEN) return false;

    const now = Date.now();
    const lastPong = this.lastPongTime || lastPongTime;
    if (lastPong > 0 && now - lastPong > 120000) {
      return false;
    }

    return true;
  }

  forceReconnect(accountSetId) {
    console.warn("WebSocket health check failed, reconnecting");
    this.reconnectAttempts = 0;

    if (this.socket) {
      this.socket.close(1000, "Force reconnect");
    }
    if (socket) {
      socket.close(1000, "Force reconnect");
    }

    setTimeout(() => {
      const nextId = getFallbackAccountSetId(accountSetId);
      if (nextId) connectWS(nextId);
    }, 1000);
  }

  startHealthCheck() {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
    }

    this.healthCheckInterval = setInterval(() => {
      const activeSocket = this.socket || socket;
      if (!activeSocket) return;
      if (!this.checkWebSocketHealth()) {
        this.forceReconnect(getFallbackAccountSetId());
      }
    }, 30000);
  }

  stopHealthCheck() {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
  }
}

const wsManager = new WebSocketManager();

const WS_BUS = (() => {
  if (typeof window !== "undefined") {
    if (!window.__WS_BUS__) {
      window.__WS_BUS__ = { handlers: new Map() };
    }
    return window.__WS_BUS__;
  }
  return { handlers: new Map() };
})();

function handleInternalApiError(payload) {
  const d = payload?.data || payload;
  const id = d?.id;
  wsManager._seenApiErrIds = wsManager._seenApiErrIds || new Set();
  if (id && wsManager._seenApiErrIds.has(id)) return;
  if (id) wsManager._seenApiErrIds.add(id);
}

function noopWsBusHandler() {}

function installInternalBusHandlers() {
  onMessage("api_error", handleInternalApiError);
  onMessage("order_profit_batch", noopWsBusHandler);
  onMessage("tp_event", noopWsBusHandler);
}

let socket = null;
let reconnectInterval = null;
let heartbeatInterval = null;
let lastPongTime = 0;

const desiredSubscriptions = {
  sets: new Set(),
  quotes: new Map(),
  premium: new Map(),
  positions: new Set(),
  openOrders: new Set(),
  orderUpdates: new Set(),
  orderProfit: new Set(),
};

function normalizeAccountSetId(accountSetId) {
  if (accountSetId === undefined || accountSetId === null || accountSetId === "") return null;
  return String(accountSetId);
}

function getFallbackAccountSetId(preferred = null) {
  const preferredId = normalizeAccountSetId(preferred);
  if (preferredId) return preferredId;
  if (wsManager.accountSetId) return wsManager.accountSetId;
  const firstDesired = desiredSubscriptions.sets.values().next();
  return firstDesired && !firstDesired.done ? firstDesired.value : null;
}

function markDesiredSet(accountSetId) {
  const asId = normalizeAccountSetId(accountSetId);
  if (!asId) return null;
  desiredSubscriptions.sets.add(asId);
  return asId;
}

function markDesiredQuote(accountSetId, futureSymbol, spotSymbol) {
  const asId = markDesiredSet(accountSetId);
  if (!asId || !futureSymbol || !spotSymbol) return null;
  desiredSubscriptions.quotes.set(asId, { futureSymbol, spotSymbol });
  return asId;
}

function markDesiredPremium(accountSetId, futureSymbol, spotSymbol) {
  const asId = markDesiredSet(accountSetId);
  if (!asId || !futureSymbol || !spotSymbol) return null;
  desiredSubscriptions.premium.set(asId, { futureSymbol, spotSymbol });
  return asId;
}

function markDesiredFlag(setRef, accountSetId) {
  const asId = markDesiredSet(accountSetId);
  if (!asId) return null;
  setRef.add(asId);
  return asId;
}

function sendIfOpen(message, targetSocket = null) {
  const activeSocket = targetSocket || wsManager.socket || socket;
  if (!activeSocket || activeSocket.readyState !== WebSocket.OPEN) return false;
  activeSocket.send(JSON.stringify(message));
  return true;
}

function replayDesiredSubscriptions(targetSocket) {
  const activeSocket = targetSocket || wsManager.socket || socket;
  if (!activeSocket || activeSocket.readyState !== WebSocket.OPEN) return;

  desiredSubscriptions.sets.forEach((accountSetId) => {
    sendIfOpen({ action: "subscribe_set", accountSetId }, activeSocket);
  });

  desiredSubscriptions.quotes.forEach(({ futureSymbol, spotSymbol }, accountSetId) => {
    sendIfOpen({ action: "subscribe_quote", accountSetId, futureSymbol, spotSymbol }, activeSocket);
  });

  desiredSubscriptions.premium.forEach(({ futureSymbol, spotSymbol }, accountSetId) => {
    sendIfOpen(
      { action: "subscribe_premium", accountSetId, futureSymbol, spotSymbol },
      activeSocket,
    );
  });

  desiredSubscriptions.positions.forEach((accountSetId) => {
    sendIfOpen({ action: "subscribe_positions", accountSetId }, activeSocket);
  });

  desiredSubscriptions.openOrders.forEach((accountSetId) => {
    sendIfOpen({ action: "subscribe_open_orders", accountSetId }, activeSocket);
  });

  desiredSubscriptions.orderUpdates.forEach((accountSetId) => {
    sendIfOpen({ action: "subscribe_order_updates", accountSetId }, activeSocket);
  });

  desiredSubscriptions.orderProfit.forEach((accountSetId) => {
    sendIfOpen({ action: "subscribe_order_profit", accountSetId }, activeSocket);
  });
}

function sendHeartbeat() {
  const activeSocket = wsManager.socket || socket;
  if (activeSocket && activeSocket.readyState === WebSocket.OPEN) {
    activeSocket.send(JSON.stringify({ action: "ping", timestamp: Date.now() }));
  }
}

function startHeartbeat() {
  if (wsManager.heartbeatInterval) clearInterval(wsManager.heartbeatInterval);
  if (heartbeatInterval) clearInterval(heartbeatInterval);

  const interval = setInterval(() => {
    const now = Date.now();
    const lastPong = wsManager.lastPongTime || lastPongTime;
    if (lastPong > 0 && now - lastPong > 90000) {
      console.warn("WebSocket heartbeat timeout");
      const activeSocket = wsManager.socket || socket;
      if (activeSocket) {
        activeSocket.close(1000, "Heartbeat timeout");
      }
      return;
    }
    sendHeartbeat();
  }, process.env.REACT_APP_WS_HEARTBEAT_INTERVAL || 30000);

  wsManager.heartbeatInterval = interval;
  heartbeatInterval = interval;
}

function stopHeartbeat() {
  if (wsManager.heartbeatInterval) {
    clearInterval(wsManager.heartbeatInterval);
    wsManager.heartbeatInterval = null;
  }
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
}

export { wsManager };
export const createBrokerKey = (broker) => wsManager.createBrokerKey(broker);
export const isQuoteFresh = (quote, tolerance) => wsManager.isQuoteFresh(quote, tolerance);
export const validateTradeQuotes = (futureQuote, spotQuote) =>
  wsManager.validateTradeQuotes(futureQuote, spotQuote);

export function onBalanceBatch(handler) {
  return onMessage("balance_batch", handler);
}

export function onQuoteBatch(handler) {
  return onMessage("quote_batch", handler);
}
export function connectWS(accountSetId) {
  const asId = getFallbackAccountSetId(accountSetId);
  if (!asId) {
    console.warn("connectWS: no accountSetId available");
    return;
  }

  const authToken = getStoredAuthToken();
  if (!authToken) {
    logWs("No auth token available, skipping WebSocket connection");
    return;
  }

  const previousSetId = wsManager.accountSetId;
  markDesiredSet(asId);
  wsManager.accountSetId = asId;
  wsManager.startHealthCheck();

  const activeSocket = wsManager.socket || socket;
  if (activeSocket) {
    if (activeSocket.readyState === WebSocket.OPEN) {
      logWs("WebSocket already open, replaying desired subscriptions for:", asId);
      if (previousSetId === asId) return;
      replayDesiredSubscriptions(activeSocket);
      return;
    }
    if (activeSocket.readyState === WebSocket.CONNECTING) {
      logWs("WebSocket is connecting, desired subscriptions recorded for:", asId);
      return;
    }
    try {
      activeSocket.close();
    } catch {}
    wsManager.socket = null;
    socket = null;
  }

  logWs("Attempting WebSocket connection for account set:", asId);

  if (wsManager.reconnectInterval) {
    clearTimeout(wsManager.reconnectInterval);
    wsManager.reconnectInterval = null;
  }
  if (reconnectInterval) {
    clearTimeout(reconnectInterval);
    reconnectInterval = null;
  }

  logWs("Connecting WebSocket for account set:", asId);

  function getWsUrl() {
    if (process.env.REACT_APP_WS_URL) {
      return process.env.REACT_APP_WS_URL;
    }

    if (process.env.REACT_APP_API_URL) {
      try {
        const apiUrl = new URL(process.env.REACT_APP_API_URL);
        const protocol = apiUrl.protocol === "https:" ? "wss:" : "ws:";
        return `${protocol}//${apiUrl.host}/ws`;
      } catch (error) {
        console.warn(
          "Invalid REACT_APP_API_URL for WS fallback:",
          process.env.REACT_APP_API_URL,
          error,
        );
      }
    }

    const protocol = window.location.protocol === "https:" ? "wss" : "ws";

    let host;
    const frontendPort = process.env.REACT_APP_FRONTEND_PORT || "3000";
    const backendPort = process.env.REACT_APP_BACKEND_PORT || "5000";

    if (window.location.hostname.includes("devtunnels.ms")) {
      host = window.location.hostname.replace(`-${frontendPort}`, `-${backendPort}`);
    } else if (process.env.NODE_ENV === "development") {
      host = `${window.location.hostname}:${backendPort}`;
    } else {
      host = window.location.host;
    }

    return `${protocol}://${host}/ws`;
  }

  const WS_URL = buildAuthenticatedWsUrl(getWsUrl(), authToken);

  logWs("WebSocket URL:", WS_URL);

  const newSocket = new WebSocket(WS_URL);
  wsManager.socket = newSocket;
  socket = newSocket;
  const currentSocket = newSocket;

  currentSocket.onopen = () => {
    logWs("WebSocket connection established");
    wsManager.reconnectAttempts = 0;
    wsManager.lastPongTime = Date.now();
    lastPongTime = Date.now();
    startHeartbeat();

    if (currentSocket.readyState === WebSocket.OPEN) {
      logWs("Replaying desired subscriptions");
      replayDesiredSubscriptions(currentSocket);
    }
  };

  currentSocket.onmessage = (event) => {
    let msg;
    try {
      msg = JSON.parse(event.data);
      if (WS_VERBOSE && msg.type !== "pong" && msg.action !== "ping") {
        console.log("WebSocket message received:", msg);
      }
    } catch (err) {
      console.error("❌ Invalid WS message format:", event.data, err);
      return;
    }

    if (msg.type === "pong" || msg.action === "pong") {
      wsManager.lastPongTime = Date.now();
      lastPongTime = Date.now();
      return;
    }

    if (msg.type === "api_error") {
      if (wsManager.shouldLogApiError(msg.data || msg)) {
        console.error("API Error (throttled):", msg.data || msg);
      }
    }

    const messagePayload = msg.type === "balance" ? msg : (msg.data ?? msg.payload ?? msg);

    if (msg.type === "balance") {
      const brokerKey = wsManager.createBrokerKey({
        id: messagePayload.brokerId,
        terminal: messagePayload.data?.terminal,
        position: messagePayload.data?.position,
      });
      if (brokerKey) {
        wsManager.balanceUpdateBuffer.set(brokerKey, messagePayload);
      }
    } else if (msg.type === "quote_update") {
      const clientReceiveTime = Date.now();
      const wsSpeed = msg.serverSendTime ? clientReceiveTime - msg.serverSendTime : null;
      if (messagePayload.futureQuote && wsSpeed !== null) {
        messagePayload.futureQuote.wsSpeed = wsSpeed;
      }
      if (messagePayload.spotQuote && wsSpeed !== null) {
        messagePayload.spotQuote.wsSpeed = wsSpeed;
      }
      wsManager.quoteUpdateBuffer[`${messagePayload.futureSymbol}_${messagePayload.spotSymbol}`] =
        messagePayload;
    }

    const bucket = WS_BUS.handlers.get(msg.type);
    const list = bucket ? Array.from(bucket) : [];
    if (list.length === 0) {
      return;
    }

    const flat = messagePayload && typeof messagePayload === "object" ? messagePayload : {};
    const enriched = {
      type: msg.type,
      accountSetId: msg.accountSetId,
      data: messagePayload,
      ...flat,
    };

    for (const fn of list) {
      try {
        fn(enriched);
      } catch (err) {
        console.error("WS handler error:", msg.type, err);
      }
    }
  };

  socket.onerror = (err) => {
    console.error("🛑 WebSocket error:", err);
  };

  currentSocket.onclose = (event) => {
    if (event.code !== 1000) {
      console.warn("WebSocket connection closed:", event.code, event.reason);
    }
    wsManager.socket = null;
    socket = null;
    stopHeartbeat();

    markProfitStale(true);
    if (event.code === 4401) {
      wsManager.reconnectAttempts = 0;
      if (wsManager.reconnectInterval) {
        clearTimeout(wsManager.reconnectInterval);
        wsManager.reconnectInterval = null;
      }
      if (reconnectInterval) {
        clearTimeout(reconnectInterval);
        reconnectInterval = null;
      }
      wsManager.stopHealthCheck();
      if (getStoredAuthToken()) {
        clearClientSession();
      }
      return;
    }

    if (event.code !== 1000 && wsManager.reconnectAttempts < wsManager.maxReconnectAttempts) {
      wsManager.reconnectAttempts++;
      const delay = Math.min(wsManager.baseReconnectDelay * wsManager.reconnectAttempts, 5000);
      logWs(
        `Attempting reconnect ${wsManager.reconnectAttempts}/${wsManager.maxReconnectAttempts} in ${delay}ms`,
      );

      wsManager.reconnectInterval = setTimeout(() => {
        const nextId = getFallbackAccountSetId(accountSetId);
        if (nextId) connectWS(nextId);
      }, delay);
      reconnectInterval = wsManager.reconnectInterval;
    } else if (wsManager.reconnectAttempts >= wsManager.maxReconnectAttempts) {
      console.error("Max WebSocket reconnection attempts reached. Please refresh the page.");
      setTimeout(() => {
        wsManager.reconnectAttempts = 0;
      }, 30000);
    }
  };
}

export function subscribeToQuotes(accountSetId, futureSymbol, spotSymbol, onUpdate) {
  logWs("subscribeToQuotes called:", {
    accountSetId,
    futureSymbol,
    spotSymbol,
    hasHandler: Boolean(onUpdate),
  });
  const asId = markDesiredQuote(accountSetId, futureSymbol, spotSymbol);
  if (!asId) return;

  connectWS(asId);

  sendIfOpen({
    action: "subscribe_quote",
    accountSetId: asId,
    futureSymbol,
    spotSymbol,
  });
}

export function subscribeToPremium(accountSetId, futureSymbol, spotSymbol) {
  const asId = markDesiredPremium(accountSetId, futureSymbol, spotSymbol);
  if (!asId) return;
  connectWS(asId);
  sendIfOpen({
    action: "subscribe_premium",
    accountSetId: asId,
    futureSymbol,
    spotSymbol,
  });
  logWs("Subscribed to premium updates for:", asId);
}

export function subscribeToPositions(accountSetId) {
  const asId = markDesiredFlag(desiredSubscriptions.positions, accountSetId);
  if (!asId) return;
  connectWS(asId);
  sendIfOpen({ action: "subscribe_positions", accountSetId: asId });
  logWs("Subscribed to positions updates for:", asId);
}

export function subscribeToOpenOrders(accountSetId) {
  const asId = markDesiredFlag(desiredSubscriptions.openOrders, accountSetId);
  if (!asId) return;
  connectWS(asId);
  sendIfOpen({ action: "subscribe_open_orders", accountSetId: asId });
  logWs("Subscribed to open orders updates for:", asId);
}

export function subscribeToOrderUpdates(accountSetId, onOrderUpdate) {
  const asId = markDesiredFlag(desiredSubscriptions.orderUpdates, accountSetId);
  if (!asId) return;

  if (onOrderUpdate) {
    onMessage("order_update", onOrderUpdate);
    onMessage("closed_trade_created", onOrderUpdate);
  }

  connectWS(asId);
  sendIfOpen({ action: "subscribe_order_updates", accountSetId: asId });
  logWs("Subscribed to order updates for:", asId);
}

export function subscribeToOrderProfit(accountSetId, onBatch) {
  const asId = markDesiredFlag(desiredSubscriptions.orderProfit, accountSetId);
  if (!asId) return () => {};
  connectWS(asId);

  (function ensureProfitSubscribed(tries = 0) {
    const ok = sendMessage({ action: "subscribe_order_profit", accountSetId: asId });
    if (!ok && tries < 10) setTimeout(() => ensureProfitSubscribed(tries + 1), 500);
  })();

  const offBatch = onMessage("order_profit_batch", (payload) => {
    const arr = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
    if (arr.length) {
      markProfitHealthy();
      debugWs("order_profit_batch -> page", { size: arr.length, sample: arr[0] });
      if (typeof onBatch === "function") {
        try {
          onBatch(arr);
        } catch (e) {
          console.error("order_profit_batch handler error:", e);
        }
      }
    }
  });

  const offSingle = onMessage("profit_update", (payload) => {
    const asid = normalizeAccountSetId(payload?.accountSetId);
    if (asid && asid !== asId) return;
    const d = payload?.data;
    if (!d) return;
    const arr = Array.isArray(d) ? d : [d];
    if (arr.length) {
      markProfitHealthy();
    }
    if (typeof onBatch === "function") {
      try {
        onBatch(
          arr.map((x) => ({
            ticket: x.ticket ?? x.Ticket,
            profit: x.profit ?? x.Profit,
            symbol: x.symbol ?? x.Symbol,
            lots: x.lots ?? x.Lots,
          })),
        );
      } catch (e) {
        console.error("profit_update handler error:", e);
      }
    }
  });

  const offTp = onMessage("tp_event", () => {});

  return () => {
    offBatch?.();
    offSingle?.();
    offTp?.();
  };
}

export function onMessage(type, fn) {
  const bucket = WS_BUS.handlers.get(type) || new Set();
  bucket.add(fn);
  WS_BUS.handlers.set(type, bucket);
  return () => offMessage(type, fn);
}

export function offMessage(type, fn) {
  const bucket = WS_BUS.handlers.get(type);
  if (!bucket) return;
  bucket.delete(fn);
  if (bucket.size === 0) WS_BUS.handlers.delete(type);
}

export function sendMessage(message) {
  const activeSocket = wsManager.socket || socket;
  if (activeSocket && activeSocket.readyState === WebSocket.OPEN) {
    activeSocket.send(JSON.stringify(message));
    return true;
  }
  debugWs("WebSocket not open, cannot send:", message);
  return false;
}

export function getWSStatus() {
  const activeSocket = wsManager.socket || socket;
  if (!activeSocket) return "disconnected";

  switch (activeSocket.readyState) {
    case WebSocket.CONNECTING:
      return "connecting";
    case WebSocket.OPEN:
      return "connected";
    case WebSocket.CLOSING:
      return "closing";
    case WebSocket.CLOSED:
      return "disconnected";
    default:
      return "unknown";
  }
}

export function isWSConnected() {
  const activeSocket = wsManager.socket || socket;
  return activeSocket && activeSocket.readyState === WebSocket.OPEN;
}

export function clearQuoteSubscriptions() {
  logWs("Clearing quote subscriptions for account set switch");
  WS_BUS.handlers.delete("quote_update");
  WS_BUS.handlers.delete("quote_batch");
  wsManager.quoteUpdateBuffer = {};
}

export function disconnectWS() {
  logWs("Disconnecting WebSocket");
  if (wsManager.reconnectInterval) {
    clearTimeout(wsManager.reconnectInterval);
    wsManager.reconnectInterval = null;
  }
  if (reconnectInterval) {
    clearTimeout(reconnectInterval);
    reconnectInterval = null;
  }

  stopHeartbeat();
  wsManager.stopHealthCheck();
  if (wsManager.socket) {
    wsManager.socket.close(1000, "Manual disconnect");
    wsManager.socket = null;
  }
  if (socket) {
    socket.close(1000, "Manual disconnect");
    socket = null;
  }

  WS_BUS.handlers.clear();
  installInternalBusHandlers();
  wsManager.reconnectAttempts = 0;
  wsManager.accountSetId = null;

  desiredSubscriptions.sets.clear();
  desiredSubscriptions.quotes.clear();
  desiredSubscriptions.premium.clear();
  desiredSubscriptions.positions.clear();
  desiredSubscriptions.openOrders.clear();
  desiredSubscriptions.orderUpdates.clear();
  desiredSubscriptions.orderProfit.clear();
}

export function subscribeToLockedAccountSets(accountSets, { connectAccountSetId = null } = {}) {
  if (!Array.isArray(accountSets) || accountSets.length === 0) {
    return { lockedSetIds: [] };
  }

  const lockedSets = accountSets.filter(
    (set) => set?.symbolsLocked && set?.futureSymbol && set?.spotSymbol,
  );

  if (lockedSets.length === 0) {
    return { lockedSetIds: [] };
  }

  const firstLockedId = lockedSets[0]?._id || lockedSets[0]?.id;
  const connectId = getFallbackAccountSetId(connectAccountSetId || firstLockedId);
  if (connectId) {
    connectWS(connectId);
  }

  lockedSets.forEach((set) => {
    const accountSetId = set?._id || set?.id;
    const asId = normalizeAccountSetId(accountSetId);
    if (!asId) return;

    markDesiredQuote(asId, set.futureSymbol, set.spotSymbol);
    markDesiredPremium(asId, set.futureSymbol, set.spotSymbol);
    markDesiredFlag(desiredSubscriptions.positions, asId);
    markDesiredFlag(desiredSubscriptions.openOrders, asId);
    markDesiredFlag(desiredSubscriptions.orderUpdates, asId);
    markDesiredFlag(desiredSubscriptions.orderProfit, asId);
  });

  replayDesiredSubscriptions();

  return {
    lockedSetIds: lockedSets.map((set) => String(set?._id || set?.id)).filter(Boolean),
  };
}

installInternalBusHandlers();

export function getProfitStreamHealth() {
  const MAX_AGE_MS = 5000;
  const age = Date.now() - (profitStreamHealth.lastUpdate || 0);
  if (age > MAX_AGE_MS) {
    profitStreamHealth.healthy = false;
  }
  return {
    healthy: profitStreamHealth.healthy,
    resubRecent: profitStreamHealth.resubRecent,
  };
}
